/**
 * Bed direction is stored on a planting as a compass bearing in degrees. Users
 * pick from the 8 principal cardinal directions, each of which maps to a fixed
 * degree value.
 */

export interface BedDirectionOption {
	degrees: number;
	label: string;
}

export const BED_DIRECTIONS: readonly BedDirectionOption[] = [
	{ degrees: 0, label: "N" },
	{ degrees: 45, label: "NE" },
	{ degrees: 90, label: "E" },
	{ degrees: 135, label: "SE" },
	{ degrees: 180, label: "S" },
	{ degrees: 225, label: "SW" },
	{ degrees: 270, label: "W" },
	{ degrees: 315, label: "NW" },
];

const LABEL_BY_DEGREES = new Map(
	BED_DIRECTIONS.map((d) => [d.degrees, d.label]),
);

/** Dropdown options: id is the stored degree value, text is the cardinal label. */
export const BED_DIRECTION_OPTIONS = BED_DIRECTIONS.map((d) => ({
	id: d.degrees,
	text: d.label,
}));

/**
 * Cardinal label for a stored bearing, for *display*. Canonical bearings show
 * their cardinal name (45 -> "NE"); any other (legacy) value falls back to the
 * raw degrees so it stays visible. Returns undefined when unset.
 */
export function bedDirectionLabel(degrees?: number | null): string | undefined {
	if (degrees === undefined || degrees === null) return undefined;
	return LABEL_BY_DEGREES.get(degrees) ?? String(degrees);
}

/**
 * The dropdown selection for a stored bearing: only canonical bearings map to a
 * selectable option, so non-canonical legacy values return undefined (shown
 * blank), mirroring how legacy plastic-color values aren't re-selectable.
 */
export function bedDirectionSelection(
	degrees?: number | null,
): number | undefined {
	return degrees !== undefined &&
		degrees !== null &&
		LABEL_BY_DEGREES.has(degrees)
		? degrees
		: undefined;
}
